'use client';

import { useCallback, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/ui/alert';
import { services } from '@repo/ui/lib/api/services';
import { useQuery } from '@tanstack/react-query';
import { useInventaire } from '../../hooks/use-inventaire';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/components/ui/tabs';
import { EpaysageBasicForm } from '../../../epaysages/forms/epaysage-basic-form';
import { EpaysageAdvancedFields } from '../../../epaysages/forms/epaysage-advanced-fields';
import { EssenceTabs } from '../../../epaysages/essence-tabs';
import { mapEpaysageToBasicInitialValues } from '../../../epaysages/utils/mappers';
import type { MapCoordinates } from '@repo/ui/features/inventaires/map-view/providers';
import type { Epaysage } from '@repo/types';
import { ImageUpload } from '../../shared/components/image-upload';
import { EssenceIllustrationPage } from '../../shared/components/essence-illustration-page';
import { toEssenceIllustrationModel } from '../../shared/components/essence-illustration-model';
import { imageService } from '@repo/ui/lib/services/image.service';
import { useEpaysageImageOperations } from '../../hooks/use-epaysage-images';

interface DetailsEpaysageProps {
  inventaireId: number;
}

export default function DetailsEpaysage({ inventaireId }: DetailsEpaysageProps) {
  const [activeTab, setActiveTab] = useState<
    'base' | 'advanced' | 'essences' | 'images' | 'illustrations'
  >('base');
  const {
    data: inventaireResponse,
    isLoading,
    error,
    refetch,
  } = useInventaire(inventaireId, { enabled: !!inventaireId, queryKey: ['inventaire', inventaireId] });

  const epaysage: Epaysage | undefined = inventaireResponse?.data?.epaysage;
  const epaysageId = epaysage?.id;
  const imageOperations = useEpaysageImageOperations();

  const { data: coordinatesResponse, isLoading: isCoordinatesLoading } = useQuery({
    queryKey: ['epaysage', epaysageId, 'coordinates'],
    queryFn: () => services.epaysage.getCoordinates(epaysageId!),
    enabled: Boolean(epaysageId),
    staleTime: 5 * 60 * 1000,
  });

  const polygonFromInventaire: MapCoordinates[] =
    Array.isArray(epaysage?.coord) && epaysage.coord.length
      ? (epaysage.coord as MapCoordinates[])
      : [];

  const coordinateData = coordinatesResponse?.data ?? polygonFromInventaire;

  const basicInitialData = useMemo(() => {
    if (!epaysage) {
      return undefined;
    }
    return mapEpaysageToBasicInitialValues(
      epaysage,
      coordinateData,
      inventaireResponse?.data?.id,
    );
  }, [coordinateData, epaysage, inventaireResponse?.data?.id]);

  const handleImageChange = useCallback(
    async (fieldName: string, value: string | File) => {
      if (!epaysageId) return;
      const field = fieldName as 'img1' | 'img2' | 'img3';

      try {
        if (value instanceof File) {
          await imageOperations.upload.mutateAsync({ epaysageId, field, file: value });
          await refetch();
        } else if (value === '') {
          await imageOperations.delete.mutateAsync({ epaysageId, field });
          await refetch();
        }
      } catch (err) {
        console.error(`Erreur lors de la mise à jour de ${field}`, err);
      }
    },
    [epaysageId, imageOperations.upload, imageOperations.delete, refetch],
  );

  const handleImageRotate = useCallback(
    async (fieldName: string, rotation: number) => {
      if (!epaysageId) return;
      const field = fieldName as 'img1' | 'img2' | 'img3';

      try {
        await imageOperations.rotate.mutateAsync({ epaysageId, field, rotation });
        await refetch();
      } catch (err) {
        console.error(`Erreur lors de la rotation de ${field}`, err);
        throw err;
      }
    },
    [epaysageId, imageOperations.rotate, refetch],
  );

  const getImageUrl = (value?: string | null) =>
    value ? imageService.getImageUrl(value, 'epaysage') : '';

  const imageConfig = {
    maxFiles: 1,
    acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    maxSize: 20,
  };

  if (isLoading || isCoordinatesLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-muted-foreground">Chargement des informations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Erreur</AlertTitle>
        <AlertDescription>
          Impossible de charger les informations de l&apos;inventaire. Veuillez réessayer plus tard.
        </AlertDescription>
      </Alert>
    );
  }

  if (!epaysage || !epaysageId || !basicInitialData) {
    return (
      <Alert>
        <AlertTitle>Espace boisé indisponible</AlertTitle>
        <AlertDescription>
          Aucun espace boisé n&apos;est associé à cet inventaire.
        </AlertDescription>
      </Alert>
    );
  }

  const resolvedInventaireId = basicInitialData.inventaireId ?? inventaireResponse?.data?.id;
  const epaysageImages = {
    img1: epaysage?.img1,
    img2: epaysage?.img2,
    img3: epaysage?.img3,
  } as const;
  const imageErrorMessage =
    imageOperations.error && imageOperations.error instanceof Error
      ? imageOperations.error.message
      : undefined;

  return (
    <div className="space-y-4">

      <Card>
        <CardHeader>
          <CardTitle>Gestion de l&apos;espace boisé</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="base">Informations de base</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
              <TabsTrigger value="advanced">Champs avancés</TabsTrigger>
              <TabsTrigger value="essences">Essences</TabsTrigger>
              <TabsTrigger value="illustrations">Illustrations</TabsTrigger>
            </TabsList>

            <TabsContent value="base" className="mt-4 space-y-4">
              <Card>
                <CardContent className="pt-6">
                  <EpaysageBasicForm
                    mode="UPDATE"
                    epaysageId={epaysageId}
                    inventaireId={resolvedInventaireId}
                    initialValues={basicInitialData.initialValues}
                    initialPolygon={basicInitialData.polygon}
                    isFinished={inventaireResponse?.data?.is_finished}
                    onSuccess={() => refetch()}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="images" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Images de l&apos;espace boisé</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Ajoutez jusqu&apos;à trois photos de l&apos;espace boisé pour enrichir la fiche.
                  </p>
                  <div className="grid gap-6 lg:grid-cols-3">
                    <ImageUpload
                      name="img1"
                      label="Image principale"
                      value={getImageUrl(epaysageImages?.img1)}
                      onChange={handleImageChange}
                      onRotate={handleImageRotate}
                      imageConfig={imageConfig}
                      error={imageErrorMessage}
                      isLoading={imageOperations.isLoading}
                    />
                    <ImageUpload
                      name="img2"
                      label="Image secondaire"
                      value={getImageUrl(epaysageImages?.img2)}
                      onChange={handleImageChange}
                      onRotate={handleImageRotate}
                      imageConfig={imageConfig}
                      error={imageErrorMessage}
                      isLoading={imageOperations.isLoading}
                    />
                    <ImageUpload
                      name="img3"
                      label="Image tertiaire"
                      value={getImageUrl(epaysageImages?.img3)}
                      onChange={handleImageChange}
                      onRotate={handleImageRotate}
                      imageConfig={imageConfig}
                      error={imageErrorMessage}
                      isLoading={imageOperations.isLoading}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="advanced" className="mt-4">
              <EpaysageAdvancedFields
                epaysage={epaysage}
                epaysageId={epaysageId}
                inventaireId={resolvedInventaireId}
                onSuccess={() => refetch()}
              />
            </TabsContent>

            <TabsContent value="essences" className="mt-4">
              <EssenceTabs epaysageId={epaysageId} onEssenceChange={() => refetch()} />
            </TabsContent>

            <TabsContent value="illustrations" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Illustrations des essences</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {(epaysage.essence ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Aucune essence renseignée sur cet espace boisé.
                    </p>
                  ) : (
                    (epaysage.essence ?? []).map((essence, index) => (
                      <div key={essence.id ?? index} className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">
                          Essence {index + 1}
                        </p>
                        <EssenceIllustrationPage
                          essences={essence.espece ? [toEssenceIllustrationModel(essence.espece)] : []}
                        />
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

